/**
 * Single-PUT upload of the Audio Sidecar (PLAT-11697).
 *
 * The Sidecar is 1–3 % of the Original (≤ ~100 MB for a 5 GB file), so one
 * `PutObject` with the same STS credentials as the video multipart is enough;
 * no worker, no progress, no visible UI. Two silent retries, then the caller
 * gives up and the upload takes the Server-Extract Path.
 */
import { PutObjectCommand, S3Client } from '@aws-sdk/client-s3';
import { LogManager } from '@src/utility/Logger';

import type { MultipartUploadCredentials } from '../MultipartCore';

const logger = LogManager.getLogger('upload:sidecar');

/** Silent retries after the first attempt (decision F). */
export const SIDECAR_UPLOAD_RETRIES = 2;
export const SIDECAR_CONTENT_TYPE = 'audio/mp4';

export interface SidecarPutTarget {
  bucket: string;
  region: string;
  key: string;
  credentials: MultipartUploadCredentials;
}

export type PutSidecarObject = (
  target: SidecarPutTarget,
  blob: Blob,
) => Promise<void>;

export type SidecarUploadOutcome =
  | { ok: true; key: string; wallMs: number; attempts: number }
  | { ok: false; attempts: number; error: unknown };

/** `${keyPrefix}${storyId}.audio.m4a`, next to the Original in the org's boundary. */
export function audioSidecarKey(keyPrefix: string, storyId: string): string {
  const prefix = keyPrefix.endsWith('/') ? keyPrefix : `${keyPrefix}/`;
  return `${prefix}${storyId}.audio.m4a`;
}

/** Default PUT: one S3 request, accelerate endpoint like the multipart path. */
export const putSidecarObjectWithS3: PutSidecarObject = async (
  target,
  blob,
) => {
  const client = new S3Client({
    region: target.region,
    credentials: {
      accessKeyId: target.credentials.accessKeyId,
      secretAccessKey: target.credentials.secretAccessKey,
      sessionToken: target.credentials.sessionToken,
    },
    useAccelerateEndpoint: true,
    // Same posture as MultipartCore (which strips ChecksumAlgorithm): no CRC
    // trailer on a Blob body.
    requestChecksumCalculation: 'WHEN_REQUIRED',
  });
  await client.send(
    new PutObjectCommand({
      Bucket: target.bucket,
      Key: target.key,
      Body: blob,
      ContentType: SIDECAR_CONTENT_TYPE,
      ContentLength: blob.size,
    }),
  );
};

export async function uploadAudioSidecar(
  blob: Blob,
  target: SidecarPutTarget,
  options: { put?: PutSidecarObject; retries?: number } = {},
): Promise<SidecarUploadOutcome> {
  const put = options.put ?? putSidecarObjectWithS3;
  const attemptsAllowed = 1 + (options.retries ?? SIDECAR_UPLOAD_RETRIES);
  const startedAt = performance.now();
  let lastError: unknown;
  for (let attempt = 1; attempt <= attemptsAllowed; attempt += 1) {
    try {
      await put(target, blob);
      return {
        ok: true,
        key: target.key,
        wallMs: Math.round(performance.now() - startedAt),
        attempts: attempt,
      };
    } catch (error) {
      lastError = error;
      logger.warn('sidecar-put-failed', {
        key: target.key,
        attempt,
        attemptsAllowed,
        message: error instanceof Error ? error.message : String(error),
      });
    }
  }
  return { ok: false, attempts: attemptsAllowed, error: lastError };
}
